// Unit conversion utilities for diving data

export type DepthUnit = 'meters' | 'feet';
export type TemperatureUnit = 'celsius' | 'fahrenheit';
export type PressureUnit = 'bar' | 'psi';
export type GasConsumptionMetric = 'rmv' | 'sac';

export interface UnitPreferences {
  depth: DepthUnit;
  temperature: TemperatureUnit;
  pressure: PressureUnit;
}

// Depth conversions
export const metersToFeet = (meters: number): number => {
  return meters * 3.28084;
};

export const feetToMeters = (feet: number): number => {
  return feet / 3.28084;
};

export const formatDepth = (depthInMeters: number, unit: DepthUnit): string => {
  if (unit === 'feet') {
    return `${metersToFeet(depthInMeters).toFixed(1)}ft`;
  }
  return `${depthInMeters.toFixed(1)}m`;
};

// Temperature conversions (API returns Kelvin, we store as Celsius)
export const kelvinToCelsius = (kelvin: number): number => {
  return kelvin - 273.15;
};

export const celsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9/5) + 32;
};

export const fahrenheitToCelsius = (fahrenheit: number): number => {
  return (fahrenheit - 32) * 5/9;
};

export const kelvinToFahrenheit = (kelvin: number): number => {
  return celsiusToFahrenheit(kelvinToCelsius(kelvin));
};

export const formatTemperature = (tempInCelsius: number, unit: TemperatureUnit): string => {
  if (unit === 'fahrenheit') {
    return `${celsiusToFahrenheit(tempInCelsius).toFixed(1)}°F`;
  }
  return `${tempInCelsius.toFixed(1)}°C`;
};

// Pressure conversions
export const barToPsi = (bar: number): number => {
  return bar * 14.5037738;
};

export const psiToBar = (psi: number): number => {
  return psi / 14.5037738;
};

export const formatPressure = (pressureInBar: number, unit: PressureUnit): string => {
  if (unit === 'psi') {
    return `${barToPsi(pressureInBar).toFixed(0)} PSI`;
  }
  return `${pressureInBar.toFixed(1)} bar`;
};

/**
 * Calculate respiratory minute volume (RMV) at the surface.
 * Formula: RMV = (Tank Volume × Pressure Consumed) / (Time × (Average Depth / 10 + 1))
 * @param tankVolumeL Tank volume in liters
 * @param startPressure Starting pressure in bar
 * @param endPressure Ending pressure in bar
 * @param diveTimeMinutes Dive time in minutes
 * @param avgDepthM Average depth in meters
 * @returns RMV in L/min
 */
export const calculateSAC = (
  tankVolumeL: number,
  startPressure: number,
  endPressure: number,
  diveTimeMinutes: number,
  avgDepthM: number
): number => {
  const pressureConsumed = startPressure - endPressure;
  const airConsumedL = tankVolumeL * pressureConsumed;
  const averageAbsolutePressure = (avgDepthM / 10) + 1; // ATA (Absolute pressure)
  const sacRate = airConsumedL / (diveTimeMinutes * averageAbsolutePressure);
  return sacRate;
};

/**
 * Convert a base RMV value to the user's selected display metric.
 * RMV is shown as L/min or ft^3/min.
 * SAC is shown as bar/min or psi/min.
 */
export const getGasConsumptionDisplayValue = (
  rmvRate: number,
  metric: GasConsumptionMetric = 'rmv',
  pressureUnit: PressureUnit = 'bar',
  tankVolumeL?: number
): number | null => {
  if (metric === 'rmv') {
    if (pressureUnit === 'psi') {
      return rmvRate * 0.0353147;
    }

    return rmvRate;
  }

  if (!tankVolumeL || tankVolumeL <= 0) {
    return null;
  }

  const sacBarPerMinute = rmvRate / tankVolumeL;
  return pressureUnit === 'psi' ? barToPsi(sacBarPerMinute) : sacBarPerMinute;
};

/**
 * Convert a user-entered gas consumption value back to the base RMV in L/min.
 * Inverse of getGasConsumptionDisplayValue.
 * @returns Base RMV in L/min, or null when the conversion needs a tank volume that is missing
 */
export const getBaseRmvFromDisplayValue = (
  displayValue: number,
  metric: GasConsumptionMetric = 'rmv',
  pressureUnit: PressureUnit = 'bar',
  tankVolumeL?: number
): number | null => {
  if (metric === 'rmv') {
    return pressureUnit === 'psi' ? displayValue / 0.0353147 : displayValue;
  }

  if (!tankVolumeL || tankVolumeL <= 0) {
    return null;
  }

  const sacBarPerMinute = pressureUnit === 'psi' ? psiToBar(displayValue) : displayValue;
  return sacBarPerMinute * tankVolumeL;
};

export const getGasConsumptionMetricLabel = (metric: GasConsumptionMetric = 'rmv'): string => {
  return metric === 'sac' ? 'SAC' : 'RMV';
};

export const getGasConsumptionMetricTitle = (metric: GasConsumptionMetric = 'rmv'): string => {
  return metric === 'sac' ? 'Surface Air Consumption' : 'Respiratory Minute Volume';
};

export const getGasConsumptionUnitLabel = (
  metric: GasConsumptionMetric = 'rmv',
  pressureUnit: PressureUnit = 'bar'
): string => {
  if (metric === 'sac') {
    return pressureUnit === 'psi' ? 'psi/min' : 'bar/min';
  }

  return pressureUnit === 'psi' ? 'ft³/min' : 'L/min';
};

export const formatGasConsumptionDisplayValue = (
  value: number,
  metric: GasConsumptionMetric = 'rmv',
  pressureUnit: PressureUnit = 'bar'
): string => {
  const decimals = metric === 'rmv' && pressureUnit === 'psi' ? 3 : 1;
  return `${value.toFixed(decimals)} ${getGasConsumptionUnitLabel(metric, pressureUnit)}`;
};

/**
 * Format gas consumption with appropriate unit based on user preferences.
 * @param rmvRate Base RMV in L/min
 * @param metric Selected display metric
 * @param pressureUnit User's preferred pressure unit
 * @param tankVolumeL Tank volume in liters, required for SAC display
 * @returns Formatted string with appropriate unit
 */
export const formatSACRate = (
  rmvRate: number,
  pressureUnit: PressureUnit = 'bar',
  metric: GasConsumptionMetric = 'rmv',
  tankVolumeL?: number
): string => {
  const displayValue = getGasConsumptionDisplayValue(rmvRate, metric, pressureUnit, tankVolumeL);

  if (displayValue === null) {
    return 'Not Available';
  }

  return formatGasConsumptionDisplayValue(displayValue, metric, pressureUnit);
};

/**
 * Get tank volume in liters for standard tank sizes
 */
export const getTankVolume = (tankSize: string): number | null => {
  const tankVolumes: Record<string, number> = {
    'AL80': 11.0,
    'AL63': 8.5,
    'AL100': 13.2,
    'Steel72': 9.6,
    'Steel100': 12.0,
    'Steel120': 15.1,
    'Steel130': 16.4,
  };
  return tankVolumes[tankSize] || null;
};

export const getDiveTankVolume = (dive: any): number | null => {
  const additionalData = dive.additionalData;

  if (!additionalData) {
    return null;
  }

  if (additionalData.tankSize === 'custom' && additionalData.customTankVolume) {
    return additionalData.customTankVolume;
  }

  if (additionalData.tankSize) {
    return getTankVolume(additionalData.tankSize);
  }

  return null;
};

/**
 * Parse duration string (e.g., "30:45" or "01:30:45") to total minutes
 */
export const parseDurationToMinutes = (duration: string): number => {
  const parts = duration.split(':').map(p => parseInt(p, 10));
  if (parts.length === 2) {
    // MM:SS format
    return parts[0] + parts[1] / 60;
  } else if (parts.length === 3) {
    // HH:MM:SS format
    return parts[0] * 60 + parts[1] + parts[2] / 60;
  }
  return 0;
};

/**
 * Calculate SAC rate using pressure consumed (simplified version for our data model)
 * @param tankVolumeL Tank volume in liters
 * @param pressureConsumed Pressure consumed in bar (already calculated as start - end)
 * @param diveTimeMinutes Dive time in minutes
 * @param avgDepthM Average depth in meters
 * @returns SAC rate in L/min
 */
export const calculateSACFromConsumed = (
  tankVolumeL: number,
  pressureConsumed: number,
  diveTimeMinutes: number,
  avgDepthM: number
): number => {
  const airConsumedL = tankVolumeL * pressureConsumed;
  const averageAbsolutePressure = (avgDepthM / 10) + 1; // ATA (Absolute pressure)
  const sacRate = airConsumedL / (diveTimeMinutes * averageAbsolutePressure);
  return sacRate;
};

/**
 * Calculate SAC rate for a dive if all required data is available
 * @param dive Dive object with all data
 * @returns SAC rate in L/min or null if data is insufficient
 */
export const calculateDiveSAC = (dive: any): number | null => {
  const additionalData = dive.additionalData;
  if (!additionalData) return null;

  // Directly entered RMV is the fallback when a pressure-based calculation isn't possible
  const manualRmv =
    typeof additionalData.manualRmv === 'number' && additionalData.manualRmv > 0
      ? additionalData.manualRmv
      : null;

  // Check if we have all required data
  if (!dive.duration || !dive.avgDepth) {
    return manualRmv;
  }

  // Get tank volume
  const tankVolume = getDiveTankVolume(dive);

  if (!tankVolume) return manualRmv;

  let pressureConsumed = additionalData.airPressureConsumed;
  if (
    additionalData.startPressure !== undefined &&
    additionalData.endPressure !== undefined &&
    additionalData.startPressure > additionalData.endPressure
  ) {
    pressureConsumed = additionalData.startPressure - additionalData.endPressure;
  }

  if (!pressureConsumed) return manualRmv;

  // Convert pressure to bar if needed
  let pressureConsumedBar = pressureConsumed;
  if (additionalData.pressureUnit === 'psi') {
    pressureConsumedBar = psiToBar(pressureConsumed);
  }

  // Parse duration to minutes
  const diveTimeMinutes = parseDurationToMinutes(dive.duration);
  if (diveTimeMinutes === 0) return manualRmv;

  // Calculate SAC rate
  const sacRate = calculateSACFromConsumed(
    tankVolume,
    pressureConsumedBar,
    diveTimeMinutes,
    dive.avgDepth
  );

  return sacRate;
};

export const calculateDiveGasConsumption = (
  dive: any,
  metric: GasConsumptionMetric = 'rmv',
  pressureUnit: PressureUnit = 'bar'
): number | null => {
  const rmvRate = calculateDiveSAC(dive);
  const tankVolume = getDiveTankVolume(dive) ?? undefined;

  if (rmvRate === null) {
    return null;
  }

  return getGasConsumptionDisplayValue(rmvRate, metric, pressureUnit, tankVolume);
};